import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { Select } from 'antd';
import { useTranslation } from 'react-i18next';
import { Editions } from '../../services';
import { setAvailableEditions, setSelectedEditionId } from '../../redux/modules/editionNavigation';
import { getEditionCalendarStatus } from '../../services/editionNavUi';
import { buildContextUrl, editionSegmentFor, resolveContextSwitchPath } from '../../routing/contextUrl';

import './EditionSelector.scss';

const editionId = (e) => String(e?._id || e?.id || '');
const hasEditionId = (editions, id) => {
    if (!id) return false;
    const want = String(id);
    return (editions || []).some((e) => editionId(e) === want);
};

const EditionSelector = ({ collapsed }) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const { pathname } = useLocation();
    const user = useSelector((state) => state.auth.user);
    const { setting } = useSelector((state) => state.settings);
    const selectedEditionId = useSelector((state) => state.editionNavigation?.selectedEditionId);
    const [isLoading, setIsLoading] = useState(false);

    const can_list_editions = Boolean(
        user?.abilities?.superadmin
        || (typeof user?.can === 'function' && user.can('read', 'editions'))
    );

    const configured = setting?.edition ? String(setting.edition) : null;

    const [editions, setEditions] = useState([]);

    const loadEditions = useCallback(() => {
        if (!can_list_editions) {
            setEditions([]);
            dispatch(setAvailableEditions([]));
            return;
        }
        setIsLoading(true);
        Editions.list({})
            .then(({ editions: data }) => {
                const list = data || [];
                setEditions(list);
                dispatch(setAvailableEditions(list));
            })
            .finally(() => setIsLoading(false));
    }, [can_list_editions, dispatch]);

    useEffect(() => {
        loadEditions();
    }, [loadEditions]);

    const options = useMemo(() => {
        return (editions || []).map((e) => {
            const value = editionId(e);
            return { value, label: value };
        });
    }, [editions]);

    const currentValueRaw = selectedEditionId || configured || (options[0]?.value ?? null);
    const currentValue = useMemo(() => {
        if (!currentValueRaw) {
            return null;
        }
        const raw = String(currentValueRaw);
        const values = new Set((options || []).map((o) => o.value));
        if (values.has(raw)) {
            return raw;
        }
        return options?.[0]?.value ?? raw;
    }, [currentValueRaw, options]);

    const selectedEdition = useMemo(
        () => (editions || []).find((e) => editionId(e) === currentValue) || null,
        [editions, currentValue]
    );

    const hasValidConfiguredEdition = useMemo(
        () => hasEditionId(editions, configured),
        [editions, configured]
    );

    // Current edition is strictly: settings.edition === edition._id (and must exist in list)
    const isViewingCurrentEdition = Boolean(
        hasValidConfiguredEdition && configured && currentValue && String(configured) === String(currentValue)
    );

    const calendarStatus = selectedEdition ? getEditionCalendarStatus(selectedEdition) : null;

    const labelRender = useCallback(
        ({ label }) => {
            let iconClass = '';
            let iconTitle = '';
            if (hasValidConfiguredEdition && configured && currentValue) {
                if (isViewingCurrentEdition) {
                    iconClass = 'fa fa-check-circle EditionSelector__stateIcon EditionSelector__stateIcon--current';
                    iconTitle = t('editions.current_badge');
                } else if (calendarStatus === 'past') {
                    iconClass = 'fa fa-lock EditionSelector__stateIcon EditionSelector__stateIcon--past';
                    iconTitle = t('editions.readonly_error');
                } else {
                    iconClass = 'fa fa-unlock EditionSelector__stateIcon EditionSelector__stateIcon--editable';
                    iconTitle = t('editions.selector.editable_unlock_hint');
                }
            }

            return (
                <span className="EditionSelector__selectedInner">
                    <span className="EditionSelector__selectedId">{label}</span>
                    {iconClass ? (
                        <i className={iconClass} title={iconTitle} aria-hidden />
                    ) : null}
                </span>
            );
        },
        [calendarStatus, configured, currentValue, hasValidConfiguredEdition, isViewingCurrentEdition, t]
    );

    const showSelector = can_list_editions && (options?.length || 0) > 1;
    if (!showSelector) {
        return null;
    }

    const onChange = (value) => {
        dispatch(setSelectedEditionId(value));
        // Reflect the edition in the URL (omitted when it is the active edition).
        // Stay on the collection when possible; strip document ids to the parent
        // (detail ids may not exist in the target edition). Reload for basename/API.
        const slug = user?.tkt_instance?.slug || null;
        if (slug) {
            const editionSeg = editionSegmentFor(value, configured);
            const target_path = resolveContextSwitchPath(pathname, '/');
            const same_page = target_path === pathname;
            const target = buildContextUrl(
                { slug, editionId: editionSeg },
                target_path,
                same_page ? (window.location.search || '') : '',
                same_page ? (window.location.hash || '') : ''
            );
            window.location.assign(target);
        } else {
            window.location.reload();
        }
    };

    return (
        <div
            className={`EditionSelector w-100${collapsed ? ' EditionSelector--collapsed' : ''}`}
            title={collapsed && currentValue ? String(currentValue) : undefined}
        >
            <Select
                showSearch
                optionFilterProp="value"
                options={options}
                value={currentValue}
                onChange={onChange}
                loading={isLoading}
                className="EditionSelector__select"
                labelRender={labelRender}
            />
        </div>
    );
};

export default EditionSelector;
